// frontend/src/scenes/admin/ReportSettings.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import Header from "../../components/Header";
import AdminBackButton from "./AdminBackButton";
import { authGet, authPut, getUser } from "../../lib/authApi";

function pickRows(resp) {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.rows)) return resp.rows;
  if (Array.isArray(resp.orgs)) return resp.orgs;
  if (Array.isArray(resp.organizations)) return resp.organizations;
  return [];
}

const DEFAULT_SETTINGS = {
  report_type: "TOX",
  layout: "multi_page",
  footer_text: "",
  disclaimer_text: "",
  include_analyte_catalog: false,
};

export default function AdminReportSettings() {
  const initialOrgId = new URLSearchParams(window.location.search).get("org") || "";
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(initialOrgId);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });

  const role = String(getUser()?.role || localStorage.getItem("role") || "").toLowerCase();
  const canWrite = ["owner", "global_admin", "superadmin", "admin"].includes(role);
  const selectedOrg = useMemo(() => orgs.find((r) => r.id === selectedOrgId) || null, [orgs, selectedOrgId]);

  const loadOrgs = async () => {
    setBusy(true);
    setErr("");
    try {
      const j = await authGet("/api/admin/organizations");
      const nextRows = pickRows(j);
      setOrgs(nextRows);
      if (nextRows.length) {
        const queryOrg = initialOrgId && nextRows.some((r) => r.id === initialOrgId) ? initialOrgId : "";
        if (!selectedOrgId || (queryOrg && selectedOrgId !== queryOrg)) setSelectedOrgId(queryOrg || nextRows[0].id);
      }
    } catch (e) {
      setErr(e.message || "Failed to load organizations.");
      setOrgs([]);
    } finally {
      setBusy(false);
    }
  };

  const loadSettings = async (orgId, reportType = settings.report_type || "TOX") => {
    if (!orgId) return;
    setBusy(true);
    setErr("");
    try {
      const resp = await authGet(`/api/admin/organizations/${encodeURIComponent(orgId)}/report-settings?report_type=${encodeURIComponent(reportType)}`);
      setSettings({ ...DEFAULT_SETTINGS, ...(resp.row || {}) });
    } catch (e) {
      setErr(e.message || "Failed to load report settings.");
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    loadOrgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedOrgId) loadSettings(selectedOrgId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId]);

  const saveSettings = async () => {
    if (!canWrite || !selectedOrgId) return;
    setBusy(true);
    setErr("");
    try {
      const resp = await authPut(`/api/admin/organizations/${encodeURIComponent(selectedOrgId)}/report-settings`, settings);
      setSettings({ ...DEFAULT_SETTINGS, ...(resp.row || {}) });
      setToast({ open: true, msg: `Report settings saved for ${selectedOrg?.name || "selected organization"}. Regenerate the PDF to see changes.`, severity: "success" });
    } catch (e) {
      setErr(e.message || "Failed to save report settings.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ flex: "1 1 auto", minHeight: 0, minWidth: 0, height: "100%", overflow: "auto", p: { xs: 1, md: 2 }, pb: 4 }}>
      <AdminBackButton />
      <Header title="Report Settings" subtitle="Configure report layout, disclaimer, footer, and report-specific options" />

      {err ? <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert> : null}
      {!canWrite ? <Alert severity="warning" sx={{ mb: 2 }}>Your role can read report settings, but only Admin or Global Admin roles can save changes.</Alert> : null}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Organization</InputLabel>
                <Select label="Organization" value={selectedOrgId} onChange={(e) => setSelectedOrgId(e.target.value)}>
                  {orgs.map((org) => <MenuItem key={org.id} value={org.id}>{org.name}{org.code ? ` (${org.code})` : ""}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Report settings are separate from Organization setup. Pick the org here, save, then regenerate the report PDF.
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {selectedOrg ? (
        <Card variant="outlined">
          <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>Report Settings / Disclaimer</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              These settings apply only to <strong>{selectedOrg.name}</strong>. The Lab Results page must be on this same organization, then generate a new PDF.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Report Type</InputLabel>
                  <Select
                    label="Report Type"
                    value={settings.report_type || "TOX"}
                    onChange={(e) => {
                      const reportType = e.target.value;
                      setSettings((s) => ({ ...s, report_type: reportType }));
                      loadSettings(selectedOrgId, reportType);
                    }}
                  >
                    <MenuItem value="TOX">TOX</MenuItem>
                    <MenuItem value="UA">UA</MenuItem>
                    <MenuItem value="BLOOD">BLOOD</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={8}>
                <FormControl fullWidth size="small">
                  <InputLabel>Report Layout</InputLabel>
                  <Select label="Report Layout" value={settings.layout || "multi_page"} onChange={(e) => setSettings((s) => ({ ...s, layout: e.target.value }))}>
                    <MenuItem value="multi_page">Multi-page report</MenuItem>
                    <MenuItem value="one_page">One-page report</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <TextField
              label="Report Disclaimer"
              helperText="Prints in the disclaimer section of the one-page report. Leave blank to use the existing default lab disclaimer."
              fullWidth
              multiline
              minRows={5}
              sx={{ mt: 2 }}
              value={settings.disclaimer_text || ""}
              onChange={(e) => setSettings((s) => ({ ...s, disclaimer_text: e.target.value }))}
            />
            <TextField
              label="Footer Override (optional)"
              helperText="Leave blank to keep the same footer as before: CLIA/LD from the facility record. Only fill this in if the org needs custom footer text."
              fullWidth
              multiline
              minRows={3}
              sx={{ mt: 2 }}
              value={settings.footer_text || ""}
              onChange={(e) => setSettings((s) => ({ ...s, footer_text: e.target.value }))}
            />
            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Checkbox checked={settings.include_analyte_catalog !== false} onChange={(e) => setSettings((s) => ({ ...s, include_analyte_catalog: e.target.checked }))} />}
              label="Include analyte catalog on one-page reports"
            />
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={saveSettings} disabled={busy}>Save Report Settings</Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Alert severity="info">Create an organization first, then return here to edit report settings.</Alert>
      )}

      <Snackbar open={toast.open} autoHideDuration={3500} onClose={() => setToast((t) => ({ ...t, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))} sx={{ width: "100%" }}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
